import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import HotelSettingsCentralMappingTablesTableMapping from 'lumen/components/hotel/settings/central-mapping/tables/table-mapping';
import SettingsCentralMappingDropdownsDropdownMappingMoreActions from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-more-actions';
import SettingsCentralMappingModalsModalMappingOnboarding from 'lumen/components/settings/central-mapping/modals/modal-mapping-onboarding';
import SettingsCentralMappingModalsModalUploadMapping from 'lumen/components/settings/central-mapping/modals/modal-upload-mapping';
import SettingsMappingModalsMappingTypeModal from 'lumen/components/settings/mapping/modals/mapping-type-modal';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type HotelSettingsCentralMappingMappingController from 'lumen/controllers/hotel/settings/central-mapping/mapping';
import type HotelSettingsCentralMappingMappingRoute from 'lumen/routes/hotel/settings/central-mapping/mapping';
import { DEFAULT_PAGE_SIZE } from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';

interface HotelSettingsCentralMappingMappingSignature {
  Args: {
    controller: HotelSettingsCentralMappingMappingController;
    model: ModelFrom<HotelSettingsCentralMappingMappingRoute>;
  };
}

<template>
  {{#if @controller.isPageLoading}}
    <PrismLoadingBar />
  {{/if}}

  <SettingsPageTemplate
    @badge={{if
      @controller.showMappingPageBadge
      @controller.mappingPageBadgeText
    }}
    @description={{if
      @controller.isStayRestrictionRateGroupBreakdown
      (t
        "settings.central_mapping.mapping.description.stay_restriction_rate_group"
      )
    }}
    @isFullHeight={{true}}
    @title={{@controller.title}}
    class="central-mapping"
    data-test-id="central-mapping-mapping-page-template"
  >
    <:breadcrumb>
      <PrismBreadcrumb as |B|>
        <B.item
          @label={{t "settings.pms_mapping.mapping"}}
          @route="hotel.settings.central-mapping.menu"
        />
        <B.item @isCurrent={{true}} @label={{@controller.title}} />
      </PrismBreadcrumb>
    </:breadcrumb>

    <:headerAfter>
      <div class="flex flex-row gap-200">
        {{#unless @controller.isPageLoading}}
          {{#if @controller.isReadOnly}}
            {{#unless
              (or
                @controller.isMappingManagedByMarket
                @controller.isStayRestrictionRateGroupBreakdown
              )
            }}
              <PrismButton
                @buttonType="ghost"
                @iconBefore="book"
                @label={{t "general.help_center"}}
                @onClick={{@controller.openHelpArticle}}
                data-test-id="central-mapping-mapping-help-center-link"
              />
            {{/unless}}

            {{#if @controller.isMappingManagedByMarket}}
              <PrismButton
                @buttonType="primary"
                @iconBefore={{if
                  @controller.hasMarketMappingEditAccess
                  "edit-pencil"
                }}
                @label={{@controller.navigateToMarketButtonLabel}}
                data-test-id="central-mapping-mapping-navigate-to-market-button"
                {{on "click" @controller.onClickNavigateToMarket}}
              />
            {{/if}}

            {{#unless @controller.isStayRestrictionRateGroupBreakdown}}
              <PrismButton
                @buttonType="secondary"
                @label={{@controller.navigateToHierarchyButtonLabel}}
                data-test-id="central-mapping-mapping-navigate-to-hierarchy-button"
                {{on "click" @controller.onClickNavigateToHierarchy}}
              />
            {{/unless}}
          {{else}}
            {{#if @controller.moreActionsOptions.length}}
              <SettingsCentralMappingDropdownsDropdownMappingMoreActions
                @dropdownOptions={{@controller.moreActionsOptions}}
                @onSelect={{@controller.onSelectMoreActionsOption}}
              />
            {{/if}}

            {{#if @controller.isMappingManagedByMarket}}
              <PrismButton
                @buttonType="secondary"
                @iconBefore={{if
                  @controller.hasMarketMappingEditAccess
                  "edit-pencil"
                }}
                @label={{@controller.navigateToMarketButtonLabel}}
                data-test-id="central-mapping-mapping-navigate-to-market-button"
                {{on "click" @controller.onClickNavigateToMarket}}
              />
            {{/if}}

            <PrismButton
              @buttonType="secondary"
              @isDisabled={{or
                @controller.isSaving
                (not @controller.hasChanges)
              }}
              @label={{t "settings.central_mapping.discard"}}
              @onClick={{@controller.onClickDiscardChanges}}
              data-test-id="central-mapping-mapping-discard-changes-button"
            />

            <PrismButton
              @isDisabled={{or
                @controller.isSaving
                (not @controller.hasChanges)
              }}
              @isLoading={{@controller.isSaving}}
              @label={{t "central_mapping.save_changes"}}
              data-test-id="central-mapping-mapping-save-changes-button"
              {{on "click" @controller.onClickSave}}
            />
          {{/if}}
        {{/unless}}
      </div>
    </:headerAfter>

    <:default>
      <HotelSettingsCentralMappingTablesTableMapping
        @appliedFilters={{@controller.appliedFilters}}
        @applyFilters={{@controller.applyMappingFilters}}
        @breakdown={{@controller.breakdown}}
        @curentPageSizeValueOption={{@controller.curentPageSizeValueOption}}
        @currentPage={{@controller.page}}
        @deepDiscountCategory={{@controller.deepDiscountCategory}}
        @endIndexPage={{or @controller.mappingPaginationData.endIndex 0}}
        @filterOption={{@controller.filterOption}}
        @hasEditRights={{not @controller.isReadOnly}}
        @hasUnsavedChanges={{@controller.hasChanges}}
        @hierarchyCategories={{@controller.hierarchy}}
        @isLoading={{@model.mapping.isRunning}}
        @isMappingReadOnly={{@controller.isReadOnly}}
        @isSaving={{@controller.isSaving}}
        @lastPage={{or @controller.mappingPaginationData.pageCount 0}}
        @mapping={{@controller.mapping}}
        @onChangePageSize={{@controller.onChangePageSize}}
        @onClickUploadMapping={{@controller.onClickUploadMapping}}
        @onSetPage={{@controller.onSetPage}}
        @pageSize={{or
          @controller.mappingPaginationData.pageSize
          DEFAULT_PAGE_SIZE
        }}
        @search={{@controller.search}}
        @selectedMappingEntries={{@controller.selectedMappingEntries}}
        @setFilterOption={{@controller.setFilterOption}}
        @setSelectedMappingEntries={{@controller.setSelectedMappingEntries}}
        @setValueType={{@controller.setValueType}}
        @startIndexPage={{or @controller.mappingPaginationData.startIndex 0}}
        @totalNoOfItems={{or
          @controller.mappingPaginationData.totalNoOfItems
          0
        }}
        @valueType={{@controller.valueType}}
      />
    </:default>
  </SettingsPageTemplate>

  {{#if @controller.showUserOnboardingModal}}
    <SettingsCentralMappingModalsModalMappingOnboarding
      @onClose={{@controller.closeUserOnboardingModal}}
    />
  {{/if}}

  {{#if
    (and
      @controller.isMappingTypeModalAvailable
      @controller.isShowingReviMappingTypeModal
    )
  }}
    <SettingsMappingModalsMappingTypeModal
      @onCloseModal={{@controller.onCloseReviMappingTypeModal}}
      @usage="central-mapping"
    />
  {{/if}}

  {{#if @controller.isShowingUploadModal}}
    <SettingsCentralMappingModalsModalUploadMapping
      @isUploadRunning={{@controller.uploadMappingTask.isRunning}}
      @onClearUploadError={{@controller.clearUploadErrorMessage}}
      @onClose={{@controller.onCloseUploadModal}}
      @onUpload={{@controller.handleUploadFile}}
      @uploadErrorMessage={{@controller.uploadErrorMessage}}
    />
  {{/if}}
</template> satisfies TOC<HotelSettingsCentralMappingMappingSignature>;
